// src/utils/gizmo/gizmoUpdateHelpers.js
//
// useGizmo.js'den cikarilmis yardimci fonksiyonlar.
// Pure math (rayIntersection2D, circumcenter) + scene graph guncelleme fonksiyonlari.

import * as THREE from 'three';

// ── Pure Math ────────────────────────────────────────────────────────────────

export function rayIntersection2D(p1, d1, p2, d2) {
    const denominator = d1.x * d2.y - d1.y * d2.x;
    if (Math.abs(denominator) < 0.0001) return null;
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const t = (dx * d2.y - dy * d2.x) / denominator;
    return new THREE.Vector3(p1.x + t * d1.x, p1.y + t * d1.y, p1.z);
}

export function circumcenter(p1, p2, p3) {
    const ax = p1.x, ay = p1.y;
    const bx = p2.x, by = p2.y;
    const cx = p3.x, cy = p3.y;
    const D = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
    if (Math.abs(D) < 1e-10) return null;
    const ux = ((ax * ax + ay * ay) * (by - cy) + (bx * bx + by * by) * (cy - ay) + (cx * cx + cy * cy) * (ay - by)) / D;
    const uy = ((ax * ax + ay * ay) * (cx - bx) + (bx * bx + by * by) * (ax - cx) + (cx * cx + cy * cy) * (bx - ax)) / D;
    return new THREE.Vector3(ux, uy, 0);
}

// ── Gizmo Scale ──────────────────────────────────────────────────────────────

export function updateGizmoScale(gizmo, camera) {
    if (!gizmo || !camera) return;
    if (camera.zoom) {
        const scale = 1 / camera.zoom;
        gizmo.scale.set(scale, scale, scale);
    }
}

export function updateAllGizmosScale(scene, camera) {
    if (!scene || !camera) return;
    scene.traverse(obj => {
        if (obj.name.startsWith("__gizmo") && obj.userData.isGizmo) {
            updateGizmoScale(obj, camera);
        }
    });
}

// ── MTEXT ────────────────────────────────────────────────────────────────────

export function rerenderMtextSprite(sprite, newWorldWidth, newWorldHeight) {
    if (!sprite.isSprite) return;
    const ud = sprite.userData;
    if (!ud || !ud.rawText) return;

    const baseFontSize = parseInt(ud.baseFontSize || '20', 10);
    const scaleFactor = baseFontSize / 20;
    const font = ud.font || 'Arial';
    const bold = ud.bold || false;
    const italic = ud.italic || false;
    const textColor = ud.color || '#ffffff';

    const width = newWorldWidth * scaleFactor;
    const height = newWorldHeight * scaleFactor;

    const dpr = window.devicePixelRatio || 1;
    const resolutionFactor = 4;

    const fontStyle = `${italic ? 'italic ' : ''}${bold ? 'bold ' : ''}${baseFontSize}px ${font}`;
    const lineHeight = baseFontSize * 1.4;
    const padding = 8;
    const maxTextWidth = Math.max(1, width - padding * 2);

    const canvas = document.createElement('canvas');
    canvas.width = width * dpr * resolutionFactor;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr * resolutionFactor, dpr * resolutionFactor);
    ctx.font = fontStyle;

    const lines = [];
    for (const paragraph of ud.rawText.split('\n')) {
        const words = paragraph.split(/\s+/);
        let line = '';
        for (const word of words) {
            const testLine = line ? line + ' ' + word : word;
            if (ctx.measureText(testLine).width > maxTextWidth && line) {
                lines.push(line);
                line = word;
            } else {
                line = testLine;
            }
        }
        if (line) lines.push(line);
    }

    const neededHeight = lineHeight * lines.length + padding * 2;
    const actualHeight = Math.max(height, neededHeight);

    canvas.width = width * dpr * resolutionFactor;
    canvas.height = actualHeight * dpr * resolutionFactor;
    ctx.scale(dpr * resolutionFactor, dpr * resolutionFactor);
    ctx.font = fontStyle;
    ctx.textBaseline = 'top';

    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, width, neededHeight);

    ctx.fillStyle = textColor;
    lines.forEach((line, i) => {
        ctx.fillText(line, padding, padding + i * lineHeight);
    });

    if (sprite.material.map) sprite.material.map.dispose();
    const texture = new THREE.CanvasTexture(canvas);
    texture.anisotropy = 16;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    sprite.material.map = texture;
    sprite.material.needsUpdate = true;

    sprite.scale.set(
        canvas.width / (scaleFactor * dpr * resolutionFactor),
        canvas.height / (scaleFactor * dpr * resolutionFactor),
        1
    );

    ud.width = width;
    ud.height = actualHeight;
}

export function updateMtextGizmos(scene, mtextObj) {
    if (!mtextObj) return;
    const mtextId = mtextObj.userData?.id;
    if (!mtextId) return;

    let corners, center;
    if (mtextObj.isSprite) {
        const p = mtextObj.position;
        const sx = mtextObj.scale.x, sy = mtextObj.scale.y;
        corners = [
            new THREE.Vector3(p.x, p.y, 0),
            new THREE.Vector3(p.x + sx, p.y, 0),
            new THREE.Vector3(p.x + sx, p.y - sy, 0),
            new THREE.Vector3(p.x, p.y - sy, 0),
        ];
        center = new THREE.Vector3(p.x + sx / 2, p.y - sy / 2, 0);
    } else {
        mtextObj.updateWorldMatrix(true, false);
        const box = new THREE.Box3().setFromObject(mtextObj);
        if (box.isEmpty()) return;
        corners = [
            new THREE.Vector3(box.min.x, box.max.y, 0),
            new THREE.Vector3(box.max.x, box.max.y, 0),
            new THREE.Vector3(box.max.x, box.min.y, 0),
            new THREE.Vector3(box.min.x, box.min.y, 0),
        ];
        center = box.getCenter(new THREE.Vector3());
    }

    scene.traverse(g => {
        if (!g.userData?.isGizmo || g.userData.mtextId !== mtextId) return;

        if (g.userData.type === 'mtextCorner') {
            g.position.copy(corners[g.userData.cornerIndex]);
        } else if (g.userData.type === 'mtextCenter') {
            g.position.copy(center);
        } else if (g.userData.type === 'mtextBorder') {
            const pos = g.geometry.attributes.position;
            const pts = [corners[0], corners[1], corners[2], corners[3], corners[0]];
            pts.forEach((pt, i) => pos.setXYZ(i, pt.x, pt.y, pt.z));
            pos.needsUpdate = true;
            g.computeLineDistances();
        }
    });
}

// ── Arc / Ellipse / Circle ───────────────────────────────────────────────────

export function updateArcGizmos(scene, draggedGizmo, origData) {
    const arcId = draggedGizmo.userData.arcId;

    let arcObj = null;
    scene.traverse(obj => {
        if (obj.isLine && obj.userData.id === arcId) arcObj = obj;
    });
    if (!arcObj) return;

    const pos = arcObj.geometry.attributes.position;
    const pStart = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
    const pEnd = new THREE.Vector3(pos.getX(pos.count - 1), pos.getY(pos.count - 1), pos.getZ(pos.count - 1));
    const pMid = new THREE.Vector3(
        pos.getX(Math.floor(pos.count / 2)),
        pos.getY(Math.floor(pos.count / 2)),
        pos.getZ(Math.floor(pos.count / 2))
    );
    const center = circumcenter(pStart, pMid, pEnd);
    if (!center) return;

    const radius = pStart.distanceTo(center);
    const startAngle = Math.atan2(pStart.y - center.y, pStart.x - center.x);
    let endAngle = Math.atan2(pEnd.y - center.y, pEnd.x - center.x);
    if (endAngle <= startAngle) endAngle += Math.PI * 2;
    const midAngle = (startAngle + endAngle) / 2;

    scene.traverse(gizmo => {
        if (
            gizmo.name.startsWith("__gizmo") &&
            gizmo.userData.isGizmo &&
            gizmo.userData.arcId === arcId &&
            gizmo !== draggedGizmo
        ) {
            if (gizmo.userData.type === 'arcCenter') {
                gizmo.position.copy(center);
            } else if (gizmo.userData.type === 'arcStart') {
                gizmo.position.copy(pStart);
            } else if (gizmo.userData.type === 'arcEnd') {
                gizmo.position.copy(pEnd);
            } else if (gizmo.userData.type === 'arcMidpoint') {
                gizmo.position.set(
                    center.x + radius * Math.cos(midAngle),
                    center.y + radius * Math.sin(midAngle),
                    center.z
                );
            }
        }
    });
}

export function updateEllipseGizmos(scene, draggedGizmo, origData) {
    const ellipseId = draggedGizmo.userData.ellipseId;

    let ellipseObj = null;
    scene.traverse(obj => {
        if (obj.isLine && obj.userData.id === ellipseId) ellipseObj = obj;
    });
    if (!ellipseObj) return;

    const pos = ellipseObj.geometry.attributes.position;
    const segments = pos.count - 1;
    const p0 = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
    const pHalf = new THREE.Vector3(
        pos.getX(Math.floor(segments / 2)),
        pos.getY(Math.floor(segments / 2)),
        pos.getZ(Math.floor(segments / 2))
    );
    const center = p0.clone().add(pHalf).multiplyScalar(0.5);
    const rotation = origData ? origData.rotation : 0;

    const radiusX = p0.distanceTo(center);
    const pQ1 = new THREE.Vector3(
        pos.getX(Math.floor(segments / 4)),
        pos.getY(Math.floor(segments / 4)),
        pos.getZ(Math.floor(segments / 4))
    );
    const radiusY = pQ1.distanceTo(center);

    const quarterAngles = [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2];
    const cosR = Math.cos(rotation);
    const sinR = Math.sin(rotation);

    scene.traverse(gizmo => {
        if (
            gizmo.name.startsWith("__gizmo") &&
            gizmo.userData.isGizmo &&
            gizmo.userData.ellipseId === ellipseId &&
            gizmo !== draggedGizmo
        ) {
            if (gizmo.userData.type === 'ellipseCenter') {
                gizmo.position.copy(center);
            } else if (gizmo.userData.type === 'ellipseQuarter') {
                const angle = quarterAngles[gizmo.userData.quarterIndex];
                const ex = radiusX * Math.cos(angle);
                const ey = radiusY * Math.sin(angle);
                gizmo.position.set(
                    center.x + ex * cosR - ey * sinR,
                    center.y + ex * sinR + ey * cosR,
                    center.z
                );
            }
        }
    });
}

export function updateCircleGizmos(scene, draggedGizmo, origData) {
    const circleId = draggedGizmo.userData.circleId;

    let circleObj = null;
    scene.traverse(obj => {
        if (obj.isLine && obj.userData.id === circleId) circleObj = obj;
    });
    if (!circleObj) return;

    const pos = circleObj.geometry.attributes.position;
    const segments = pos.count - 1;
    const p0 = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
    const pHalf = new THREE.Vector3(
        pos.getX(Math.floor(segments / 2)),
        pos.getY(Math.floor(segments / 2)),
        pos.getZ(Math.floor(segments / 2))
    );
    const center = p0.clone().add(pHalf).multiplyScalar(0.5);
    const radius = p0.distanceTo(center);

    const quarterAngles = [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2];

    scene.traverse(gizmo => {
        if (
            gizmo.name.startsWith("__gizmo") &&
            gizmo.userData.isGizmo &&
            gizmo.userData.circleId === circleId &&
            gizmo !== draggedGizmo
        ) {
            if (gizmo.userData.type === 'circleCenter') {
                gizmo.position.copy(center);
            } else if (gizmo.userData.type === 'circleQuarter') {
                const angle = quarterAngles[gizmo.userData.quarterIndex];
                gizmo.position.set(
                    center.x + radius * Math.cos(angle),
                    center.y + radius * Math.sin(angle),
                    center.z
                );
            }
        }
    });
}

// ── Line / Wire Gizmos ──────────────────────────────────────────────────────

export function updateGizmos(scene, draggedGizmo, camera = null) {
    const { lineId } = draggedGizmo.userData;

    scene.traverse(obj => {
        if (obj.isLine && obj.userData.id === lineId) {
            const pos = obj.geometry.attributes.position;

            scene.traverse(gizmo => {
                if (
                    gizmo.name.startsWith("__gizmo") &&
                    gizmo.userData.isGizmo &&
                    gizmo.userData.lineId === lineId &&
                    gizmo !== draggedGizmo
                ) {
                    const type = gizmo.userData.type;

                    if (type === "vertex") {
                        const vertexIndex = gizmo.userData.vertexIndex;
                        const x = pos.getX(vertexIndex);
                        const y = pos.getY(vertexIndex);
                        const z = pos.getZ(vertexIndex);
                        gizmo.position.set(x, y, z);
                    } else if (type === "midpoint") {
                        const edgeIndex = gizmo.userData.edgeIndex;
                        if (edgeIndex < pos.count - 1) {
                            const start = new THREE.Vector3(pos.getX(edgeIndex), pos.getY(edgeIndex), pos.getZ(edgeIndex));
                            const end = new THREE.Vector3(pos.getX(edgeIndex + 1), pos.getY(edgeIndex + 1), pos.getZ(edgeIndex + 1));
                            const midpoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
                            gizmo.position.copy(midpoint);
                        }
                    } else if (type === "start") {
                        gizmo.position.set(pos.getX(0), pos.getY(0), pos.getZ(0));
                    } else if (type === "end") {
                        const last = pos.count - 1;
                        gizmo.position.set(pos.getX(last), pos.getY(last), pos.getZ(last));
                    } else if (type === "center") {
                        const start = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
                        const last = pos.count - 1;
                        const end = new THREE.Vector3(pos.getX(last), pos.getY(last), pos.getZ(last));
                        const c = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
                        gizmo.position.copy(c);
                    }
                }
            });

            // Wire marker'ları güncelle
            scene.traverse(markerGroup => {
                if (
                    markerGroup.userData?.role === 'wireMarker' &&
                    markerGroup.userData?.parentWireId === lineId &&
                    markerGroup.userData?.edgeIndex !== undefined
                ) {
                    const edgeIndex = markerGroup.userData.edgeIndex;
                    if (edgeIndex < pos.count - 1) {
                        const start = new THREE.Vector3(pos.getX(edgeIndex), pos.getY(edgeIndex), pos.getZ(edgeIndex));
                        const end = new THREE.Vector3(pos.getX(edgeIndex + 1), pos.getY(edgeIndex + 1), pos.getZ(edgeIndex + 1));
                        const dir = new THREE.Vector3().subVectors(end, start);
                        const normal = dir.clone().normalize();
                        const angleRad = THREE.MathUtils.degToRad(45);
                        const angledDir = new THREE.Vector3(
                            normal.x * Math.cos(angleRad) - normal.y * Math.sin(angleRad),
                            normal.x * Math.sin(angleRad) + normal.y * Math.cos(angleRad),
                            0
                        );
                        const midpoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
                        const forwardOffset = normal.clone().setLength(3);
                        const halfLength = 2.5;
                        const positions = [
                            midpoint.clone().sub(forwardOffset),
                            midpoint.clone(),
                            midpoint.clone().add(forwardOffset),
                        ];
                        markerGroup.children.forEach((line, idx) => {
                            if (line.isLine && positions[idx]) {
                                const centerPoint = positions[idx];
                                const newStart = centerPoint.clone().add(angledDir.clone().multiplyScalar(-halfLength));
                                const newEnd = centerPoint.clone().add(angledDir.clone().multiplyScalar(halfLength));
                                line.geometry.setFromPoints([newStart, newEnd]);
                            }
                        });
                        markerGroup.userData.lastEdgeAngle = Math.atan2(dir.y, dir.x) + angleRad;
                        markerGroup.userData.midPoint = midpoint.clone();
                    }
                }
            });
        }
    });
}
